import { useEffect, useMemo } from 'react'
import { QueryKey, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useOnlineStatus } from './useOnlineStatus'
import { readSessionCacheEnvelope, writeSessionCache } from '../lib/sessionCache'

type OfflineFirstQueryOptions<TQueryFnData, TError, TData> = {
  queryKey: QueryKey
  queryFn: () => Promise<TQueryFnData>
  cacheKey: string
  cacheMaxAgeMs?: number
  enabled?: boolean
  select?: (data: TQueryFnData) => TData
} & Omit<
  UseQueryOptions<TQueryFnData, TError, TData, QueryKey>,
  'queryKey' | 'queryFn' | 'enabled' | 'initialData' | 'initialDataUpdatedAt' | 'select'
>

export function useOfflineFirstQuery<TQueryFnData, TData = TQueryFnData, TError = Error>(
  options: OfflineFirstQueryOptions<TQueryFnData, TError, TData>
): UseQueryResult<TData, TError> {
  const isOnline = useOnlineStatus()

  const {
    queryKey,
    queryFn,
    cacheKey,
    cacheMaxAgeMs,
    enabled,
    select,
    ...rest
  } = options

  const cached = useMemo(() => {
    return readSessionCacheEnvelope<TData>(cacheKey, cacheMaxAgeMs)
  }, [cacheKey, cacheMaxAgeMs])

  const query = useQuery<TQueryFnData, TError, TData, QueryKey>({
    queryKey,
    queryFn,
    enabled: Boolean(enabled ?? true) && isOnline,
    initialData: cached?.data as TQueryFnData | undefined,
    initialDataUpdatedAt: cached?.savedAt,
    select: select as ((data: TQueryFnData) => TData) | undefined,
    ...rest,
  })

  useEffect(() => {
    if (query.data === undefined) return
    writeSessionCache(cacheKey, query.data)
  }, [cacheKey, query.data])

  return query
}
